// Typed wrapper around /api/v1/users/me.
//
// Keeping the call here (rather than inlined in features/profile) means
// the same fetcher can be reused by ProtectedRoute for its
// auth-validation ping AND by the profile feature for rendering — both
// hit the same endpoint, both want the same shape, and both should
// share a single TypeScript contract.

import { apiClient } from './client';

// Per-call options that control how the apiClient reacts to auth
// failures. Today the only knob is ``skipAuthBounce`` — when true, a
// 401 from this endpoint will NOT trigger the central
// bounce-to-WP-login flow in the axios response interceptor. That is
// used by ``ProtectedRoute`` so anonymous visitors who land on
// /dashboard directly are shown the in-app "You are not authorized"
// screen instead of being silently redirected off-site.
export interface FetchCurrentUserOptions {
    skipAuthBounce?: boolean;
}

// Mirrors backend.app.schemas.shema_user.UserResponse — keep these two
// in sync whenever the API contract changes.
//
// ``roles`` are the WordPress role slugs ("administrator",
// "pmpro_role_3", "pmpro_role_4", …) sourced live from the verified WP
// profile on every /users/me call. The SPA's <ProtectedRoute /> uses
// them — together with ``membership_level`` — to decide whether the
// caller may render the internal dashboard.
export interface UserProfile {
    id: number;
    wp_id: number | null;
    email: string;
    full_name: string | null;
    membership_level: string | null;
    is_active: boolean | null;
    roles: string[];
}

export async function fetchCurrentUser(
    options: FetchCurrentUserOptions = {},
): Promise<UserProfile> {
    const { data } = await apiClient.get<UserProfile>('/users/me', {
        // ``skipAuthBounce`` is a custom field on the axios request
        // config — see the module augmentation in api/client.ts and the
        // response interceptor that reads it.
        skipAuthBounce: options.skipAuthBounce,
    });
    return data;
}
